import React, {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    MouseEvent,
    TouchEvent,
    useState,
} from 'react';
import {
    Analysis,
    Cursor,
    GhostBan,
    GhostBanOptionsParams,
    Ki,
    Theme,
    empty,
    zeros,
} from 'ghostban';
import {usePrevious} from 'react-use';
import {GHOSTBAN_OPTIONS, useDeepCompareEffect} from '@utils';
import {StoneAudioPlayer} from './StoneAudioPlayer';
import {OverlayTrigger, Tooltip} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';

export type BoardRefType = {
    getBoard: () => GhostBan | null;
    getMoveAudio: () => HTMLAudioElement | null;
    playMoveSound: () => void;
};

type BoardOptions = {
    moveSound?: boolean;
    moveSoundVolume?: number;
};

export type BoardProps = {
    boardOptions?: GhostBanOptionsParams & BoardOptions;
    boardClassName?: string;
    mat?: number[][];
    analysis?: Analysis;
    visibleAreaMat?: number[][];
    preventMoveMat?: number[][];
    effectMat?: string[][];
    markup?: string[][];
    cursor?: Cursor;
    currentMarkupText?: string;
    turn?: Ki;
    disabled?: boolean;
    size?: string | number;
    overlay?: React.ReactNode;
    onClick?: (
        e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
    ) => void;
    onRightClick?: () => void;
    onReady?: (board: GhostBan) => void;
};

const BoardRef = (
    {
        boardOptions,
        boardClassName,
        mat = zeros([19, 19]),
        analysis,
        visibleAreaMat = zeros([19, 19]),
        preventMoveMat = zeros([19, 19]),
        effectMat = empty([19, 19]),
        markup = empty([19, 19]),
        cursor = Cursor.None,
        currentMarkupText = '',
        turn = Ki.Empty,
        disabled = false,
        size = '100vmin',
        overlay,
        onClick,
        onRightClick,
        onReady,
    }: BoardProps,
    ref: React.Ref<unknown> | undefined
) => {
    const {t} = useTranslation();
    const boardDomRef = useRef<HTMLDivElement>(null);
    const boardRef = useRef<GhostBan | undefined>(undefined);
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const options = {...GHOSTBAN_OPTIONS, ...boardOptions};
        const board = new GhostBan(options);
        boardRef.current = board;
        const boardElement = boardDomRef.current;
        if (boardElement) {
            board.init(boardElement);
            onReady?.(board);
        }

        if (!boardElement || typeof ResizeObserver === 'undefined') return;

        let previousWidth = boardElement.clientWidth;
        const resizeObserver = new ResizeObserver(() => {
            const nextWidth = boardElement.clientWidth;
            if (nextWidth === previousWidth) return;

            previousWidth = nextWidth;
            board.resize();
        });
        resizeObserver.observe(boardElement);

        return () => resizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const board = boardRef.current;
        if (!board) return;

        if (mat) board.setMat(mat);
        if (markup) board.setMarkup(markup);
        if (cursor) {
            board.setCursor(cursor, currentMarkupText);
        }
        if (turn) board.setTurn(turn);
        if (visibleAreaMat) {
            board.setVisibleAreaMat(visibleAreaMat);
            board.zoomBoard(board.options.zoom);
        }

        board.render();
    }, [mat, markup, cursor, turn, visibleAreaMat, currentMarkupText]);

    // Put analysis outside of the main useEffect for mat/markup to avoid unnecessary rendering
    useEffect(() => {
        const board = boardRef.current;
        if (!board) return;

        if (analysis) {
            // setAnalysis will trigger analysis canvas rendering
            // So there is no need to call board.render() here
            board.setAnalysis(analysis);
        } else {
            board.setAnalysis(null);
        }
    }, [analysis]);

    useEffect(() => {
        const board = boardRef.current;
        if (!board) return;
        if (!preventMoveMat) return;

        board.setPreventMoveMat(preventMoveMat);
    }, [preventMoveMat]);

    useEffect(() => {
        const board = boardRef.current;
        if (!board) return;
        if (!effectMat) return;

        board.setEffectMat(effectMat);
    }, [effectMat]);

    useEffect(() => {
        document.body.style.overscrollBehavior = 'none';

        return () => {
            document.body.style.overscrollBehavior = '';
        };
    }, []);

    const prevBoardOptions = usePrevious(boardOptions);
    useDeepCompareEffect(() => {
        const board = boardRef.current;
        if (!board || !boardOptions) return;

        board.setOptions(boardOptions);

        if (prevBoardOptions && boardOptions) {
            const changedKeys = Object.keys(boardOptions).filter(key => {
                return (
                    JSON.stringify(
                        boardOptions[key as keyof typeof boardOptions]
                    ) !==
                    JSON.stringify(
                        prevBoardOptions[key as keyof typeof prevBoardOptions]
                    )
                );
            });

            if (changedKeys.length > 0) {
                const keysNeedRender = ['coordinate', 'zoom'];
                if (changedKeys.some(key => keysNeedRender.includes(key))) {
                    board.render();
                }
                // DEBUG:
                // console.log('Changed boardOptions fields:');
                // changedKeys.forEach(key => {
                //     console.log(`- ${key}:`, {
                //         from: prevBoardOptions[
                //             key as keyof typeof prevBoardOptions
                //         ],
                //         to: boardOptions[key as keyof typeof boardOptions],
                //     });
                // });
            }
        }
    }, [boardOptions]);

    const theme = boardOptions?.theme;
    const boardSize = boardOptions?.size;
    useEffect(() => {
        const board = boardRef.current;
        if (!board || !boardSize) return;

        board.setBoardSize(boardSize);
    }, [boardSize]);

    useEffect(() => {
        const board = boardRef.current;
        if (!board || !theme) return;

        const preloadThemeImages = async (themeToSet: Theme) => {
            const themeResources = GHOSTBAN_OPTIONS.themeResources;
            if (!themeResources || !(themeToSet in themeResources)) {
                board.setTheme(themeToSet);
                board.render();
                return;
            }

            const resources =
                themeResources[themeToSet as keyof typeof themeResources];
            const imagesToLoad: string[] = [];

            if ('board' in resources && resources.board) {
                imagesToLoad.push(resources.board);
            }
            if (resources.blacks) imagesToLoad.push(...resources.blacks);
            if (resources.whites) imagesToLoad.push(...resources.whites);

            if (imagesToLoad.length === 0) {
                board.setTheme(themeToSet);
                board.render();
                return;
            }

            const imagePromises = imagesToLoad.map(src => {
                return new Promise<void>((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => resolve();
                    img.onerror = () =>
                        reject(new Error(`Failed to load image: ${src}`));
                    img.src = src;
                });
            });

            try {
                await Promise.all(imagePromises);
            } catch (error) {
                console.warn(
                    'Some theme images failed to load, setting theme anyway:',
                    error
                );
            }
            board.setTheme(themeToSet);
            board.render();
        };

        void preloadThemeImages(theme);
    }, [theme]);

    useImperativeHandle(
        ref,
        () => ({
            getBoard: () => {
                return boardRef.current;
            },
            getMoveAudio: () => {
                return audioRef.current;
            },
            playMoveSound: () => {
                void audioRef.current?.play();
            },
        }),
        []
    );

    const handleClick = (
        e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
    ) => {
        if (!disabled && onClick) onClick(e);
    };

    const handleRightClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!disabled && onRightClick) onRightClick();
    };

    const style = !boardClassName ? {width: size, height: size} : {};

    const isYunziMonkeyDark = boardOptions?.theme === Theme.YunziMonkeyDark;

    const [showTooltip, setShowTooltip] = useState(false);

    const copyrightTooltip = (
        <Tooltip id="copyright-tooltip" className="text-left text-xs">
            <div className="leading-tight text-left">
                <div>{t('board_themes.yunzi_monkey_dark')}</div>
                <a
                    className="inline-block link"
                    href="https://patreon.com/goskins"
                    target="_blank"
                    rel="noopener noreferrer">
                    {t('board_themes.more_skins_by_pumu')}
                </a>
            </div>
        </Tooltip>
    );

    return (
        <>
            <StoneAudioPlayer
                sound={boardOptions?.moveSound}
                volume={boardOptions?.moveSoundVolume}
                ref={audioRef}
            />
            <div style={{position: 'relative', display: 'inline-block'}}>
                <div
                    className={`${boardClassName}`}
                    style={style}
                    ref={boardDomRef}
                    onTouchEnd={handleClick}
                    onClick={handleClick}
                    onContextMenu={handleRightClick}>
                    {overlay}
                </div>
                {isYunziMonkeyDark && (
                    <OverlayTrigger
                        placement="top"
                        overlay={copyrightTooltip}
                        show={showTooltip}
                        onToggle={setShowTooltip}
                        rootClose
                        trigger={['click']}>
                        <div className="absolute bottom-1 left-1 w-3.5 h-3.5 bg-body bg-opacity-30 text-body rounded-full flex items-center justify-center text-xs font-bold cursor-pointer z-[1000] border border-white border-opacity-30 opacity-50">
                            i
                        </div>
                    </OverlayTrigger>
                )}
            </div>
        </>
    );
};

export const Board = forwardRef<BoardRefType, BoardProps>(BoardRef);
